import { useMemo } from "react"
import { get, isEmpty, isEqual, compact, isFunction, chain, isNil, omit } from "lodash"
import { usePositionSetMetadata } from "resources/position-sets"
import { INSTRUMENTS, INSTRUMENTS_BY_MODE, INSTRUMENT_TYPES, END_VS_START_MODES } from "connected-components/dashboard/helpers"
import {
  createIntradayContributorsQuery,
  createIntradayStandaloneContributorsQuery,
} from "gatherers/contributors/intraday"
import { unwrapValue } from "helpers/assets/unwrapValue"
import { VALUE_FORMATS } from "helpers/metrics/constants"
import { getInstrumentTypeCharacter } from "helpers/metrics/utils"
import { getExcelValue } from "helpers/csv"
import { buildContributorKey } from "helpers/contributors"
import { OK } from "helpers/data-statuses"
import { LONG_SHORT_GROUPING, OVERWEIGHT_UNDERWEIGHT_GROUPING } from "helpers/rollups"

export function getContributorLabel(contributor) {
  const tickerPart = contributor.assetClass === "equity"
    ? contributor.id.split(".")[0]
    : contributor.id

  if (!tickerPart) {
    return "Unknown"
  }

  const countryPart = contributor.country && contributor.country !== "US"
    ? ` ${contributor.country}`
    : ""

  return tickerPart + countryPart
}

// [TODO] explore the possibility of using Symbol. Does weird things with for..in loops.
export const NOT_HELD = "notheld"

function getBetaInstrumentType(instrument) {
  if (instrument.type) {
    return instrument.type
  }
  if (instrument.securityId) {
    return "ETF"
  }
  if (instrument.resource) {
    return instrument.resource.type
  }
  if (instrument.benchmarkId) {
    return "BENCHMARK"
  }
  if (instrument.indexId) {
    return "INDEX"
  }
  return null
}

export function usePositionSetBetaInstrument(positionSetMetadata = {}, modelMetadata = {}) {
  const psBetaInstrument = useMemo(() => {
    if (!positionSetMetadata.betaInstrument) {
      return null
    }
    const instrument = positionSetMetadata.betaInstrument?.resource || positionSetMetadata.betaInstrument
    const type = getBetaInstrumentType(instrument)
    switch (type) {
      case "INDEX":
        return { ...instrument, id: instrument.indexId, type }
      case "ETF":
        return { ...instrument, ...instrument.securityId, id: instrument.securityId.internalId, type }
      case "BENCHMARK":
        return { ...instrument, id: instrument.benchmarkId, type }
      default:
        return { ...instrument, id: instrument.alias, type }
    }
  }, [positionSetMetadata])

  const date = modelMetadata?.date || modelMetadata?.availability?.currentDate
  // TODO [APP-2592] - remove the 4th parameter once corrected
  const [betaInstrument, betaInstrumentStatus] = usePositionSetMetadata(modelMetadata?.id, psBetaInstrument, date, "beta-instrument")
  return useMemo(() => {
    if (betaInstrumentStatus !== OK || !betaInstrument) {
      return null
    }

    return {
      ...psBetaInstrument,
      ...betaInstrument,
      description: betaInstrument.description || betaInstrument.name,
    }
  }, [betaInstrumentStatus, betaInstrument, psBetaInstrument])
}

export function getGatherersFromInstruments(availableInstruments, handleGetInstrumentGatherers) {
  return Object.entries(availableInstruments).reduce((acc, [instrumentType, instrument]) => {
    const suffix = INSTRUMENTS[instrumentType].metricsSuffix
    return [...acc, ...handleGetInstrumentGatherers(instrument, suffix, instrumentType)]
  }, [])
}

// Companion of INTRADAY_PRICE_COLUMN_DATA_PATHS (helpers/metrics/index.js): a
// priced column must appear in both — here by metric id (price stream
// subscription), there by dataPath (header "as of" tooltip). A sync test in
// __test__/intraday-price-metric-lists.test.js guards the pairing.
export const INTRADAY_PRICE_STREAM_METRIC_IDS = new Set([
  "intraday.prices",
  "intraday.percentChange",
])

const STANDALONE_METRICS_BY_PERIOD = [
  {
    period: 1,
    periodKeySuffix: "1d",
    metricIds: [
      "intraday.standaloneReturn1d",
      "intraday.standaloneFactorsReturn1d",
      "intraday.standaloneSpecificReturn1d",
    ],
  },
  {
    period: 5,
    periodKeySuffix: "1w",
    metricIds: [
      "intraday.standaloneReturn1w",
      "intraday.standaloneFactorsReturn1w",
      "intraday.standaloneSpecificReturn1w",
    ],
  },
  {
    period: 30,
    periodKeySuffix: "1m",
    metricIds: [
      "intraday.standaloneReturn1m",
      "intraday.standaloneFactorsReturn1m",
      "intraday.standaloneSpecificReturn1m",
    ],
  },
]

function shouldIncludePriceGatherers(selectedMetricSet) {
  for (const metricId of INTRADAY_PRICE_STREAM_METRIC_IDS) {
    if (selectedMetricSet.has(metricId)) {
      return true
    }
  }
  return false
}

function getStandalonePeriodsToInclude(selectedMetricSet) {
  return STANDALONE_METRICS_BY_PERIOD
    .filter(({ metricIds }) => metricIds.some(metricId => selectedMetricSet.has(metricId)))
}

function getIntradayPriceGatherers(modelId, keySuffix) {
  return [
    createIntradayContributorsQuery({
      modelId,
      prefix: `intraday${keySuffix}`,
    }),
  ]
}

function getIntradayStandaloneGatherers(modelId, periodsToInclude, keySuffix) {
  return periodsToInclude.map(({ period, periodKeySuffix }) => {
    return createIntradayStandaloneContributorsQuery({
      modelId,
      prefix: `intradayStandalone${periodKeySuffix}${keySuffix}`,
      period,
    })
  })
}

function getIntradayGatherersForInstrument(modelId, instrumentType, includePriceGatherers, standalonePeriodsToInclude) {
  const keySuffix = get(INSTRUMENTS, [instrumentType, "metricsSuffix"], "")
  const priceGatherers = includePriceGatherers ? getIntradayPriceGatherers(modelId, keySuffix) : []
  const standaloneGatherers = getIntradayStandaloneGatherers(modelId, standalonePeriodsToInclude, keySuffix)
  return [...priceGatherers, ...standaloneGatherers]
}

export function getIntradayGatherers(availableInstruments, modelId, selectedMetrics = [], { forcePriceGatherers = false } = {}) {
  if (!modelId || !availableInstruments) return []

  const selectedMetricSet = new Set(selectedMetrics.map(String))
  const includePriceGatherers = forcePriceGatherers || shouldIncludePriceGatherers(selectedMetricSet)
  const standalonePeriodsToInclude = getStandalonePeriodsToInclude(selectedMetricSet)

  const gatherers = Object.entries(availableInstruments).flatMap(([instrumentType]) => getIntradayGatherersForInstrument(modelId, instrumentType, includePriceGatherers, standalonePeriodsToInclude))

  if (forcePriceGatherers) {
    return gatherers.map((gatherer) =>
      gatherer.streamType === "prices" ? { ...gatherer, freshnessOnly: true } : gatherer,
    )
  }
  return gatherers
}

// This regex is used to strip the suffix from the metric id
// For example, if the metric id is "risk-tertiary.riskDecomposition", the suffix is "-tertiary"
// So the stripped id is "risk.riskDecomposition"
const idSuffixesRegex = chain(INSTRUMENTS)
  .map(({ suffix }) => suffix)
  .compact()
  .join("|")
  .thru((regex) => new RegExp(`${regex}$`))
  .value()

export function getStrippedId(metric) {
  return (metric.id || "").replace(idSuffixesRegex, "")
}

function shouldShowDownloadMetric(metric, existingMetrics) {
  const key = getDownloadDedupKey(metric)
  if (!key) {
    return true
  }

  return !existingMetrics.has(key)
}

function makeDownloadHeaders(metrics) {
  // If a particular metric or header should only be shown once in the download, even across multiple instruments,
  // we need to keep track of the root metric/header and skip its subsequent variants.
  const seenMetrics = {}
  return metrics.reduce((downloadHeaders, metric) => {
    const key = getDownloadDedupKey(metric)
    if (!key || !seenMetrics[key]) {
      downloadHeaders.push(metric.downloadLabel || metric.label)
    }
    if (key) {
      seenMetrics[key] = true
    }
    return downloadHeaders
  }, [])
}

export function getMetricValue(metric, item) {
  let value
  if (isFunction(metric.dataGetter)) {
    value = metric.dataGetter(item.values, { dataPath: metric.dataPath, label: item.label })
  } else if (isFunction(metric.downloadDataGetter)) {
    value = metric.downloadDataGetter(item.values, { dataPath: metric.dataPath, label: item.label })
  } else if (metric.dataPath) {
    value = get(item.values, metric.dataPath)
  } else {
    value = get(item, metric.id)
  }
  return unwrapValue(value)
}

export function formatMetricValue(value, metric) {
  if (value === "" || isNil(value)) {
    return ""
  }
  const format = metric.format || metric.outputFormat
  const isPercent = format === VALUE_FORMATS.PERCENT || format === VALUE_FORMATS.PERCENT_100
  const adjustedValue = format === VALUE_FORMATS.PERCENT_100 ? value / 100 : value
  return isNaN(adjustedValue) ? adjustedValue : getExcelValue(adjustedValue, { percent: isPercent })
}

function getDownloadDedupKey(metric) {
  return metric.onlyShowOnceInDownload ? getStrippedId(metric) : null
}

function makeDeduplicatedMetricRow(metrics, position) {
  const seenMetricIds = new Set()
  return chain(metrics)
    .map(metric => {
      if (!shouldShowDownloadMetric(metric, seenMetricIds)) {
        return null
      }
      const key = getDownloadDedupKey(metric)
      if (key) {
        seenMetricIds.add(key)
      }
      const value = getMetricValue(metric, position)
      return formatMetricValue(value, metric)
    })
    .filter(value => {
      return !isNil(value)
    })
    .value()
}

function makeDownloadRows(data, metrics, shouldFlatten) {
  if (isEmpty(data)) {
    return []
  }

  const flattenedLeafIds = new Set()

  return data.flatMap((item) => {
    if (shouldFlatten && item.children) {
      return Object.values(item.children).reduce((acc, position) => {
        if (position.children) {
          return acc
        }

        if (position.id) {
          flattenedLeafIds.add(position.id)
        }

        acc.push(makeDeduplicatedMetricRow(metrics, position))
        return acc
      }, [])
    }

    if (shouldFlatten && item.id && flattenedLeafIds.has(item.id)) {
      return []
    }

    return [makeDeduplicatedMetricRow(metrics, item)]
  })
}

export function makeAssetViewDownloadData(values) {
  if (isEmpty(values)) {
    return null
  }
  const { data: metricData, metrics, shouldFlatten } = values
  return {
    data: {
      headers: makeDownloadHeaders(metrics),
      rows: compact(makeDownloadRows(metricData, metrics, shouldFlatten)),
    },
  }
}

// marginal and marginalContribution are used interchangeably based on OP templates vs custom metrics
const METRICS_WITHOUT_MAGNITUDE_ORDERING = ["marginal", "marginalContribution", "beta", "summary"]

export const COLUMN_ORDER_OPTIONS_ID = {
  CUSTOM: "custom",
  DEFAULT: "default",
}

export function useColumnOrderOptions(selectedView, selectedMode) {
  return useMemo(() => {
    if (METRICS_WITHOUT_MAGNITUDE_ORDERING.includes(selectedView)) {
      return { options: null, order: null }
    }
    const canOrderByMagnitude = selectedView !== "custom"

    const instruments = INSTRUMENTS_BY_MODE[selectedMode] ?? []
    const isEndVsStartDateComparison = END_VS_START_MODES.includes(selectedMode)
    const isCompareMode = instruments.length > 1
    const defaultOrder = isCompareMode ? INSTRUMENT_TYPES.delta : instruments[0]

    // isCompareWeightStrategy/isOptimization omitted — threading those requires instrumentsWithMetadata;
    // optimization/weight-strategy modes will show C/Δ instead of T/Sel/Siz (acceptable for now).
    const magnitudeOptions = isCompareMode
      ? instruments.map((instrumentType) => ({
        id: instrumentType,
        primaryText: `Biggest Contributor (${getInstrumentTypeCharacter(instrumentType, isEndVsStartDateComparison)})`,
        secondaryText: "Ordered high to low by contribution to this instrument",
      }))
      : [{
        id: defaultOrder,
        primaryText: "Biggest Contributor",
        secondaryText: "Ordered high to low by contribution to this instrument",
      }]

    const options = [{
      id: COLUMN_ORDER_OPTIONS_ID.DEFAULT,
      primaryText: "Default",
      secondaryText: "The view's default ordering",
    }, ...(canOrderByMagnitude ? magnitudeOptions : []), {
      id: COLUMN_ORDER_OPTIONS_ID.CUSTOM,
      primaryText: "Custom",
      secondaryText: "Your saved column order from drag and drop",
    }]

    return { options, order: canOrderByMagnitude ? defaultOrder : "default" }
  }, [selectedView, selectedMode])
}

// nextColumnOrder is undefined when no correction is needed — never call setColumnOrder then.
export function computeColumnOrderCorrection({ columnOrder, options, order }) {
  if ([COLUMN_ORDER_OPTIONS_ID.DEFAULT, COLUMN_ORDER_OPTIONS_ID.CUSTOM].includes(columnOrder)) {
    return { nextColumnOrder: undefined }
  }

  if (!options || !order) {
    return { nextColumnOrder: undefined }
  }

  const isStillValidOption = options.some((option) => option.id === columnOrder)
  if (isStillValidOption) {
    return { nextColumnOrder: undefined }
  }

  return { nextColumnOrder: order }
}

export { getTemplateMetricIds, getViewDuplicateMetricIds } from "./get-template-metric-ids"

// Decides whether the asset view's selected grouping needs correcting, given the current
// active state and available options. Returns { nextGroupBy }: nextGroupBy is undefined when no
// change is needed (never call setGroupBy in that case), so a caller can tell "no-op" apart from
// "explicitly clear to null".
export function computeGroupByCorrection({ groupBy, isActive, hasMultipleUniqueInstruments, availableGroupByOptions }) {
  if (!groupBy) {
    return { nextGroupBy: undefined }
  }

  if (isActive && groupBy.id === "LONG_SHORT") {
    return { nextGroupBy: OVERWEIGHT_UNDERWEIGHT_GROUPING }
  }

  if (!isActive && groupBy.id === "OVERWEIGHT_UNDERWEIGHT") {
    return { nextGroupBy: LONG_SHORT_GROUPING }
  }

  const isAttributeOrTag = groupBy.isTag || groupBy.isPositionAttribute
  if ((isActive || hasMultipleUniqueInstruments) && isAttributeOrTag) {
    return { nextGroupBy: null }
  }

  if (availableGroupByOptions && availableGroupByOptions.length > 0) {
    // Ignore disabled/disabledTooltip when comparing - a stored preference may lack them. Safe
    // only because every disabled rollup is already caught by an earlier branch above; a new
    // one needs its own branch here too, or it'll silently fall through uncleared.
    const identity = value => omit(value, ["disabled", "disabledTooltip"])
    const groupByIdentity = identity(groupBy)
    const matchesGroupBy = option => isEqual(identity(option), groupByIdentity)
    const isGroupByAvailable = availableGroupByOptions.some(rollup =>
      rollup.options
        ? rollup.options.some(matchesGroupBy)
        : matchesGroupBy(rollup),
    )
    if (!isGroupByAvailable) {
      return { nextGroupBy: null }
    }
  }

  return { nextGroupBy: undefined }
}

export function hasPerformanceMetrics(view, isCustomTemplate, customSelectedMetrics) {
  // Performance is excluded — its own header already renders the control unconditionally.
  if (view === "summary") {
    return true
  }
  return isCustomTemplate && Boolean(customSelectedMetrics?.some(metric => metric.prefix === "performance"))
}

export function useHighlightedTradeRowIds(trades, rebalancedInstrument) {
  return useMemo(() => {
    if (!rebalancedInstrument) {
      return []
    }
    return trades.map(trade => {
      // Trades don't have modelProviderId, but the contributor id depends on it for indexes
      const security = {
        ...trade.security,
        ...(trade.security.assetClass === "index" && { modelProviderId: `i.${trade.security.id}` }),
      }
      return buildContributorKey(security)
    })
  }, [trades, rebalancedInstrument])
}
